import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, Sparkle, Copy, ArrowsClockwise, WhatsappLogo, Bed, Warning, Plus, Trash, X, Gear } from '@phosphor-icons/react';
import { dateIT } from '../lib/dateIT';
import ConfigureTaskDialog from '../components/ConfigureTaskDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CleaningPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [properties, setProperties] = useState([]);
  const [links, setLinks] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterProp, setFilterProp] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [manualDraft, setManualDraft] = useState({ property_id: '', room_id: '', check_in: '', check_out: '', num_guests: 2, notes: '' });
  const [reportPreview, setReportPreview] = useState(null);
  const [configureTask, setConfigureTask] = useState(null);
  const [showPastTasks, setShowPastTasks] = useState(false);
  const recentDoneRef = useRef(new Set());

  const fetchAll = useCallback(async () => {
    try {
      const [tRes, rRes, pRes, lRes, sRes] = await Promise.all([
        axios.get(`${API}/cleaning/tasks`, { withCredentials: true }),
        axios.get(`${API}/cleaning/rooms`, { withCredentials: true }),
        axios.get(`${API}/properties`, { withCredentials: true }),
        axios.get(`${API}/cleaning/staff-links`, { withCredentials: true }),
        axios.get(`${API}/cleaning/stats`, { withCredentials: true }),
      ]);
      setTasks(tRes.data || []);
      setRooms(rRes.data || []);
      setProperties(pRes.data || []);
      setLinks(lRes.data || []);
      setStats(sRes.data);
    } catch (e) {
      console.error('Fetch cleaning error', e);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // SSE realtime: tinge in verde + toast quando lo staff preme Fatto
  useEffect(() => {
    if (!user?.id) return;
    const url = `${API}/cleaning/stream`;
    const es = new EventSource(url, { withCredentials: true });
    es.addEventListener('task_done', (ev) => {
      try {
        const d = JSON.parse(ev.data);
        recentDoneRef.current.add(d.task_id);
        setTasks(prev => prev.map(t => t.id === d.task_id ? { ...t, status: 'fatto', completed_at: d.completed_at } : t));
        toast.success('✓ Stanza completata dallo staff', { duration: 4500 });
        setTimeout(() => recentDoneRef.current.delete(d.task_id), 6000);
      } catch {}
    });
    es.addEventListener('task_report', () => {
      toast.warning('Nuova segnalazione dallo staff');
      fetchAll();
    });
    es.onerror = () => { /* auto-reconnect by browser */ };
    return () => es.close();
  }, [user?.id, fetchAll]);

  const propById = (id) => properties.find(p => (p.id || p._id) === id);
  const roomById = (id) => rooms.find(r => r.id === id);

  // Today / future = visibili sempre · Passate = nascoste in dropdown
  const todayStr = new Date().toISOString().slice(0, 10);
  const baseFiltered = tasks.filter(t => {
    if (filterProp !== 'all' && t.property_id !== filterProp) return false;
    if (filterStatus !== 'all' && t.status !== filterStatus) return false;
    return true;
  });
  const filteredTasks = baseFiltered.filter(t => (t.check_out || '') >= todayStr);
  const pastTasks = baseFiltered.filter(t => (t.check_out || '') < todayStr);

  const onSetCleaningType = async (propertyId, type) => {
    try {
      await axios.patch(`${API}/properties/${propertyId}/cleaning-type`, { cleaning_type: type }, { withCredentials: true });
      toast.success('Tipo struttura aggiornato');
      fetchAll();
    } catch (e) { toast.error('Errore'); }
  };

  const onAddRoom = async (propertyId, name, max_occupancy) => {
    if (!name) return;
    try {
      await axios.post(`${API}/cleaning/rooms`, { property_id: propertyId, name, max_occupancy: Number(max_occupancy) || 2 }, { withCredentials: true });
      toast.success('Stanza creata');
      fetchAll();
    } catch (e) { toast.error('Errore'); }
  };

  const onDeleteRoom = async (id) => {
    if (!window.confirm('Eliminare la stanza?')) return;
    await axios.delete(`${API}/cleaning/rooms/${id}`, { withCredentials: true });
    fetchAll();
  };

  const onUpdateTask = async (id, patch) => {
    await axios.patch(`${API}/cleaning/tasks/${id}`, patch, { withCredentials: true });
    setTasks(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t));
  };

  const onDeleteTask = async (id) => {
    if (!window.confirm('Eliminare questo turno?')) return;
    await axios.delete(`${API}/cleaning/tasks/${id}`, { withCredentials: true });
    setTasks(prev => prev.filter(t => t.id !== id));
  };

  const onCreateManual = async () => {
    if (!manualDraft.property_id || !manualDraft.check_in || !manualDraft.check_out) {
      toast.error('Compila struttura e date');
      return;
    }
    const prop = propById(manualDraft.property_id);
    if (prop?.cleaning_type === 'stanze' && !manualDraft.room_id) {
      toast.error('Seleziona la stanza');
      return;
    }
    try {
      await axios.post(`${API}/cleaning/tasks`, manualDraft, { withCredentials: true });
      toast.success('Prenotazione aggiunta');
      setShowManualDialog(false);
      setManualDraft({ property_id: '', room_id: '', check_in: '', check_out: '', num_guests: 2, notes: '' });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const onCreateLink = async (scope, propertyId = null, name = '') => {
    try {
      await axios.post(`${API}/cleaning/staff-links`, { scope, property_id: propertyId, name }, { withCredentials: true });
      toast.success('Link creato');
      fetchAll();
    } catch (e) { toast.error('Errore'); }
  };

  const onRegenerateLink = async (id) => {
    if (!window.confirm('Rigenerare il token? Il vecchio link non funzionerà più.')) return;
    await axios.post(`${API}/cleaning/staff-links/${id}/regenerate`, {}, { withCredentials: true });
    toast.success('Token rigenerato');
    fetchAll();
  };

  const onDeleteLink = async (id) => {
    if (!window.confirm('Eliminare il link?')) return;
    await axios.delete(`${API}/cleaning/staff-links/${id}`, { withCredentials: true });
    fetchAll();
  };

  const linkUrl = (token) => `${window.location.origin}/cleaning/${token}`;
  const copyLink = (token) => {
    navigator.clipboard.writeText(linkUrl(token));
    toast.success('Link copiato');
  };
  const shareWhatsApp = (token, propName) => {
    const text = encodeURIComponent(`Ciao! Apri questo link per gestire le pulizie di ${propName || 'la struttura'}:\n${linkUrl(token)}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const propsWithCleaning = properties.filter(p => p.cleaning_type);

  return (
    <div className="min-h-screen bg-zinc-50">
      <main className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
        {/* Page header */}
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkle size={20} weight="bold" className="text-emerald-600" />
              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-700">Modulo Premium</p>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-900">Gestione Pulizie</h1>
            <p className="text-xs text-zinc-500 mt-1">Coordina lo staff · realtime · zero email</p>
          </div>
          <HostWebAppCard />
        </div>

        {/* Staff links — pinned top */}
        <section className="bg-white border-2 border-emerald-500 p-5 sm:p-6 mb-6" data-testid="staff-links-section">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <p className="text-[11px] font-black uppercase tracking-[0.22em] text-emerald-700">Link pubblico staff</p>
            <div className="flex gap-2">
              <Button onClick={() => onCreateLink('all', null, 'Tutte le strutture')} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white text-[10px] font-black uppercase tracking-wider h-8 px-3" data-testid="create-master-link">
                + Link master
              </Button>
            </div>
          </div>
          {links.length === 0 && (
            <p className="text-xs text-zinc-500">Nessun link. Crea un "Link master" per dare allo staff accesso a tutte le strutture, oppure uno per struttura.</p>
          )}
          <div className="space-y-2">
            {links.map(l => (
              <div key={l.id} className="bg-emerald-50 border border-emerald-200 p-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`link-${l.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-zinc-900">{l.name} <span className="text-[10px] text-emerald-700 font-normal">· {l.scope === 'all' ? 'tutte le strutture' : (propById(l.property_id)?.name || 'struttura')}</span></p>
                  <p className="text-[11px] font-mono text-zinc-500 truncate">{linkUrl(l.token)}</p>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  <button onClick={() => copyLink(l.token)} className="inline-flex items-center gap-1 bg-white border border-zinc-300 hover:border-zinc-900 px-2 py-1 text-[10px] font-black uppercase tracking-wider" data-testid={`copy-${l.id}`}><Copy size={11} /> Copia</button>
                  <button onClick={() => shareWhatsApp(l.token, l.scope === 'all' ? null : propById(l.property_id)?.name)} className="inline-flex items-center gap-1 bg-emerald-600 text-white hover:bg-emerald-700 px-2 py-1 text-[10px] font-black uppercase tracking-wider"><WhatsappLogo size={11} weight="bold" /> WhatsApp</button>
                  <button onClick={() => onRegenerateLink(l.id)} className="inline-flex items-center gap-1 bg-white border border-amber-300 text-amber-700 hover:border-amber-500 px-2 py-1 text-[10px] font-black uppercase tracking-wider"><ArrowsClockwise size={11} /> Rigenera</button>
                  <button onClick={() => onDeleteLink(l.id)} className="inline-flex items-center bg-white border border-rose-300 text-rose-700 hover:border-rose-500 px-2 py-1"><Trash size={11} /></button>
                </div>
              </div>
            ))}
          </div>
          {properties.length > 0 && (
            <details className="mt-3">
              <summary className="text-[10px] font-black uppercase tracking-wider text-zinc-500 cursor-pointer">+ Crea link per singola struttura</summary>
              <div className="mt-2 space-y-1">
                {properties.map(p => (
                  <button key={p.id || p._id} onClick={() => onCreateLink('property', p.id || p._id, `Link · ${p.name}`)} className="w-full text-left px-3 py-2 text-xs bg-zinc-50 hover:bg-zinc-100 border border-zinc-200" data-testid={`create-link-${p.id || p._id}`}>
                    → {p.name} · {p.property_city}
                  </button>
                ))}
              </div>
            </details>
          )}
        </section>

        {/* Stats */}
        {stats && (
          <section className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6" data-testid="stats-bar">
            {[
              { label: 'Oggi', total: stats.today.total, done: stats.today.done, color: 'emerald' },
              { label: 'Settimana', total: stats.week.total, done: stats.week.done, color: 'blue' },
              { label: 'Mese', total: stats.month.total, done: stats.month.done, color: 'violet' },
              { label: 'Segnalazioni', total: stats.reports_total, done: null, color: 'rose' },
            ].map((s, i) => (
              <div key={i} className="bg-white border border-zinc-200 p-3 sm:p-4">
                <p className="text-[9px] font-black uppercase tracking-wider text-zinc-500 mb-1">{s.label}</p>
                <p className="text-2xl font-black text-zinc-900 tabular-nums">{s.done !== null ? `${s.done}/${s.total}` : s.total}</p>
                {s.done !== null && s.total > 0 && (
                  <div className="h-1 bg-zinc-100 mt-2"><div className={`h-full bg-${s.color}-500`} style={{ width: `${Math.round(s.done / s.total * 100)}%` }} /></div>
                )}
              </div>
            ))}
          </section>
        )}

        {/* Property config */}
        <section className="bg-white border border-zinc-200 p-5 mb-6">
          <p className="text-[11px] font-black uppercase tracking-[0.22em] text-zinc-700 mb-3">Configurazione strutture</p>
          {properties.length === 0 ? (
            <p className="text-xs text-zinc-500">Aggiungi prima una struttura dalla sezione "I miei alloggi".</p>
          ) : (
            <div className="space-y-3">
              {properties.map(p => {
                const pid = p.id || p._id;
                const propRooms = rooms.filter(r => r.property_id === pid);
                return (
                  <div key={pid} className="border border-zinc-200 p-3">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                      <p className="text-sm font-bold text-zinc-900">{p.name} <span className="text-[11px] text-zinc-500 font-normal">· {p.property_city}</span></p>
                      <div className="flex gap-1">
                        {['appartamento', 'stanze'].map(t => (
                          <button key={t} onClick={() => onSetCleaningType(pid, t)} className={`px-3 py-1 text-[10px] font-black uppercase tracking-wider border-2 ${p.cleaning_type === t ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-500 border-zinc-300 hover:border-zinc-900'}`} data-testid={`type-${pid}-${t}`}>
                            {t === 'appartamento' ? '🏠 Appartamento' : '🛏️ Stanze'}
                          </button>
                        ))}
                      </div>
                    </div>
                    {p.cleaning_type === 'stanze' && (
                      <div className="mt-2 pt-2 border-t border-zinc-100">
                        <RoomsList propertyId={pid} rooms={propRooms} onAdd={onAddRoom} onDelete={onDeleteRoom} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Tasks */}
        <section className="bg-white border border-zinc-200 p-5" data-testid="tasks-section">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.22em] text-zinc-700">Turni pulizia · oggi e prossimi · {filteredTasks.length}</p>
              <p className="text-[10px] text-zinc-400 mt-0.5">Le pulizie passate sono nascoste — apri "Pulizie passate" qui sotto per vederle.</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {propsWithCleaning.length > 1 && (
                <select value={filterProp} onChange={(e) => setFilterProp(e.target.value)} className="h-8 px-2 border border-zinc-300 text-xs">
                  <option value="all">Tutte le strutture</option>
                  {propsWithCleaning.map(p => <option key={p.id || p._id} value={p.id || p._id}>{p.name}</option>)}
                </select>
              )}
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="h-8 px-2 border border-zinc-300 text-xs">
                <option value="all">Tutti gli stati</option>
                <option value="da_fare">Da fare</option>
                <option value="fatto">Completati</option>
              </select>
              <Button onClick={() => setShowManualDialog(true)} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white text-[10px] font-black uppercase tracking-wider h-8 px-3" data-testid="add-manual-btn">
                <Plus size={11} weight="bold" className="mr-1" /> Aggiungi manuale
              </Button>
            </div>
          </div>

          {filteredTasks.length === 0 && <p className="text-xs text-zinc-500 py-6 text-center">Nessun turno · Sincronizza iCal o aggiungine uno manuale</p>}

          <div className="divide-y divide-zinc-100">
            {filteredTasks.map(t => (
              <TaskRow
                key={t.id}
                task={t}
                prop={propById(t.property_id)}
                room={roomById(t.room_id)}
                rooms={rooms}
                justDone={recentDoneRef.current.has(t.id)}
                onUpdateTask={onUpdateTask}
                onDeleteTask={onDeleteTask}
                onOpenReport={setReportPreview}
                onConfigure={setConfigureTask}
              />
            ))}
          </div>

          {pastTasks.length > 0 && (
            <div className="mt-6 border-t border-zinc-200 pt-4" data-testid="past-tasks-section">
              <button
                onClick={() => setShowPastTasks(v => !v)}
                className={`w-full flex items-center justify-between gap-2 px-4 py-3 border-2 transition-all ${
                  showPastTasks
                    ? 'border-zinc-900 bg-zinc-900 text-white'
                    : 'border-zinc-300 hover:border-zinc-900 text-zinc-700 bg-white'
                }`}
                data-testid="past-tasks-toggle"
                aria-expanded={showPastTasks}
              >
                <span className="inline-flex items-center gap-2">
                  <span className={`inline-block w-2 h-2 rounded-full ${showPastTasks ? 'bg-emerald-400' : 'bg-zinc-400'}`} />
                  <span className="text-[11px] font-black uppercase tracking-[0.18em]">Pulizie passate</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 ${showPastTasks ? 'bg-white/20 text-white' : 'bg-zinc-100 text-zinc-600'}`}>
                    {pastTasks.length}
                  </span>
                </span>
                <svg
                  width="14" height="14" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="3"
                  strokeLinecap="round" strokeLinejoin="round"
                  className={`transition-transform ${showPastTasks ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {showPastTasks && (
                <div className="mt-3 divide-y divide-zinc-100 opacity-80 animate-in fade-in slide-in-from-top-1 duration-200" data-testid="past-tasks-list">
                  {pastTasks.map(t => (
                    <TaskRow
                      key={t.id}
                      task={t}
                      prop={propById(t.property_id)}
                      room={roomById(t.room_id)}
                      rooms={rooms}
                      justDone={false}
                      onUpdateTask={onUpdateTask}
                      onDeleteTask={onDeleteTask}
                      onOpenReport={setReportPreview}
                      onConfigure={setConfigureTask}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      </main>

      {/* Manual task dialog */}
      {showManualDialog && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="manual-dialog">
          <div className="bg-white max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
              <h3 className="text-sm font-black uppercase tracking-wider">Nuova prenotazione manuale</h3>
              <button onClick={() => setShowManualDialog(false)} className="p-1"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <Label className="text-[10px] font-black uppercase tracking-wider">Struttura</Label>
                <select value={manualDraft.property_id} onChange={(e) => setManualDraft({ ...manualDraft, property_id: e.target.value, room_id: '' })} className="w-full h-9 px-2 border border-zinc-300 text-sm" data-testid="manual-prop">
                  <option value="">—</option>
                  {propsWithCleaning.map(p => <option key={p.id || p._id} value={p.id || p._id}>{p.name}</option>)}
                </select>
              </div>
              {manualDraft.property_id && propById(manualDraft.property_id)?.cleaning_type === 'stanze' && (
                <div>
                  <Label className="text-[10px] font-black uppercase tracking-wider">Stanza</Label>
                  <select value={manualDraft.room_id} onChange={(e) => setManualDraft({ ...manualDraft, room_id: e.target.value })} className="w-full h-9 px-2 border border-zinc-300 text-sm" data-testid="manual-room">
                    <option value="">—</option>
                    {rooms.filter(r => r.property_id === manualDraft.property_id).map(r => <option key={r.id} value={r.id}>{r.name} (max {r.max_occupancy})</option>)}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] font-black uppercase tracking-wider">Check-in</Label>
                  <Input type="date" value={manualDraft.check_in} onChange={(e) => setManualDraft({ ...manualDraft, check_in: e.target.value })} className="rounded-none" data-testid="manual-ci" />
                </div>
                <div>
                  <Label className="text-[10px] font-black uppercase tracking-wider">Check-out</Label>
                  <Input type="date" value={manualDraft.check_out} onChange={(e) => setManualDraft({ ...manualDraft, check_out: e.target.value })} className="rounded-none" data-testid="manual-co" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-black uppercase tracking-wider">N° ospiti</Label>
                <Input type="number" min="1" value={manualDraft.num_guests} onChange={(e) => setManualDraft({ ...manualDraft, num_guests: Number(e.target.value) })} className="rounded-none" data-testid="manual-guests" />
              </div>
              <div>
                <Label className="text-[10px] font-black uppercase tracking-wider">Note staff (opzionale)</Label>
                <textarea rows={2} value={manualDraft.notes} onChange={(e) => setManualDraft({ ...manualDraft, notes: e.target.value })} className="w-full border border-zinc-300 p-2 text-sm resize-none" placeholder="Es. cambio lenzuola tutte le stanze" />
              </div>
            </div>
            <div className="px-5 py-4 border-t border-zinc-200 flex justify-end gap-2">
              <Button onClick={() => setShowManualDialog(false)} variant="outline" className="rounded-none text-xs font-bold uppercase tracking-wider">Annulla</Button>
              <Button onClick={onCreateManual} className="rounded-none bg-zinc-900 text-white text-xs font-bold uppercase tracking-wider" data-testid="manual-save">Crea</Button>
            </div>
          </div>
        </div>
      )}

      {/* Configure task dialog */}
      {configureTask && (
        <ConfigureTaskDialog
          task={configureTask}
          prop={propById(configureTask.property_id)}
          rooms={rooms}
          onClose={() => setConfigureTask(null)}
          onSaved={(updated) => {
            setTasks(prev => prev.map(x => x.id === updated.id ? { ...x, ...updated } : x));
          }}
        />
      )}

      {/* Report preview */}
      {reportPreview && <ReportPreview id={reportPreview} onClose={() => setReportPreview(null)} />}
    </div>
  );
}

function RoomsList({ propertyId, rooms, onAdd, onDelete }) {
  const [name, setName] = useState('');
  const [max, setMax] = useState(2);
  return (
    <div>
      <p className="text-[10px] font-black uppercase tracking-wider text-zinc-500 mb-2">Stanze · {rooms.length}</p>
      <div className="space-y-1 mb-2">
        {rooms.map(r => (
          <div key={r.id} className="flex items-center justify-between bg-zinc-50 px-2 py-1.5 text-xs">
            <span><Bed size={12} weight="bold" className="inline mr-1.5 text-zinc-500" />{r.name} · max {r.max_occupancy}</span>
            <button onClick={() => onDelete(r.id)} className="text-zinc-400 hover:text-rose-600" data-testid={`del-room-${r.id}`}><Trash size={11} /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-1.5">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome stanza (es. Camera Vista Mare)" className="rounded-none h-8 text-xs" data-testid={`new-room-name-${propertyId}`} />
        <Input type="number" min="1" value={max} onChange={(e) => setMax(e.target.value)} className="rounded-none h-8 text-xs w-20" placeholder="Max" />
        <Button onClick={() => { onAdd(propertyId, name, max); setName(''); }} className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-black uppercase tracking-wider h-8 px-3" data-testid={`add-room-${propertyId}`}><Plus size={11} /></Button>
      </div>
    </div>
  );
}

function ReportPreview({ id, onClose }) {
  const [report, setReport] = useState(null);
  useEffect(() => {
    if (id === 'pending') { setReport({ pending: true }); return; }
    axios.get(`${API}/cleaning/reports/${id}`, { withCredentials: true }).then(r => setReport(r.data)).catch(() => setReport(null));
  }, [id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="report-preview">
      <div className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <h3 className="text-sm font-black uppercase tracking-wider text-rose-700 inline-flex items-center gap-1.5"><Warning size={14} weight="fill" /> Segnalazione staff</h3>
          <button onClick={onClose} className="p-1"><X size={18} /></button>
        </div>
        <div className="p-5">
          {!report && <p className="text-sm text-zinc-500">Caricamento…</p>}
          {report?.pending && <p className="text-sm text-zinc-500">Aggiorna la pagina per vedere il dettaglio</p>}
          {report && !report.pending && (
            <>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {(report.categories || []).map(c => <span key={c} className="text-[10px] font-black uppercase tracking-wider px-2 py-1 bg-rose-100 text-rose-800 border border-rose-200">{c}</span>)}
              </div>
              {report.text && <p className="text-sm text-zinc-700 mb-3 leading-relaxed bg-zinc-50 p-3 border-l-4 border-zinc-300">{report.text}</p>}
              {(report.photos || []).length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {report.photos.map((p, i) => (
                    <a key={i} href={p} target="_blank" rel="noopener noreferrer" className="block aspect-square border border-zinc-200 hover:border-zinc-900">
                      <img src={p} alt="" className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-zinc-400 mt-3">Inviata il {report.created_at?.slice(0, 10)}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function HostWebAppCard() {
  const url = `${typeof window !== 'undefined' ? window.location.origin : ''}/pulizie-app`;
  const copy = () => {
    navigator.clipboard.writeText(url);
    toast.success('Link WebApp copiato');
  };
  const share = () => {
    const text = encodeURIComponent(`La tua WebApp Pulizie Host:\n${url}\n\nApri il link, accedi una volta e salvalo nella home dello smartphone per usarlo come app dedicata.`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };
  return (
    <div className="bg-emerald-50 border-2 border-emerald-500 p-3 max-w-sm w-full sm:w-auto" data-testid="host-webapp-card">
      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-700 mb-1.5 inline-flex items-center gap-1">
        <Sparkle size={11} weight="fill" /> WebApp Host
      </p>
      <p className="text-[11px] text-zinc-600 mb-2 leading-snug">Apri da qualunque dispositivo · "Aggiungi a Home" per usarla come app dedicata.</p>
      <div className="flex items-center gap-1 mb-2">
        <input
          type="text"
          readOnly
          value={url}
          onFocus={(e) => e.target.select()}
          className="flex-1 min-w-0 h-8 px-2 text-[11px] font-mono text-zinc-700 border border-emerald-300 bg-white truncate"
          data-testid="host-webapp-url"
          aria-label="URL WebApp Host"
        />
        <button
          onClick={copy}
          className="h-8 px-2.5 bg-white border border-emerald-300 hover:border-emerald-600 text-[10px] font-black uppercase tracking-wider text-emerald-700"
          data-testid="copy-host-webapp-url"
          aria-label="Copia link"
        >
          <Copy size={11} weight="bold" />
        </button>
      </div>
      <div className="flex gap-1.5">
        <a
          href="/pulizie-app"
          className="flex-1 inline-flex items-center justify-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-black uppercase tracking-wider px-3 py-2 transition-colors"
          data-testid="open-host-webapp"
        >
          <Sparkle size={11} weight="fill" /> Apri
        </a>
        <button
          onClick={share}
          className="inline-flex items-center justify-center gap-1 bg-white border border-emerald-300 hover:border-emerald-600 text-emerald-700 text-[10px] font-black uppercase tracking-wider px-3 py-2"
          data-testid="share-host-webapp"
        >
          <WhatsappLogo size={11} weight="bold" /> WhatsApp
        </button>
      </div>
    </div>
  );
}


function TaskRow({ task: t, prop, room, rooms, justDone, onUpdateTask, onDeleteTask, onOpenReport, onConfigure }) {
  const isDone = t.status === 'fatto';
  const isManual = t.source === 'manuale';
  const needsRoom = prop?.cleaning_type === 'stanze' && !t.room_id;
  const needsGuests = !t.num_guests;
  const hasExtras = (t.extras || []).length > 0;
  const needsConfig = !isDone && (needsRoom || needsGuests);

  const extraLabels = {
    culla: '🍼 Culla',
    lettino_viaggio: '🧳 Lettino viaggio',
    letti_divisi: '✂️ Letti divisi',
  };

  return (
    <div className={`py-3 ${isDone ? 'bg-emerald-50' : ''} ${justDone ? 'ring-2 ring-emerald-500 animate-pulse' : ''}`} data-testid={`task-row-${t.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-1">
            <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 ${isManual ? 'bg-blue-100 text-blue-700' : 'bg-violet-100 text-violet-700'}`}>{isManual ? 'Manuale' : `iCal · ${t.platform || ''}`}</span>
            {isDone && <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 bg-emerald-600 text-white inline-flex items-center gap-0.5"><CheckCircle size={9} weight="fill" /> Fatto</span>}
            {!isDone && needsRoom && <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-800">⚠ Stanza da assegnare</span>}
            {!isDone && needsGuests && <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-800">⚠ Ospiti mancanti</span>}
            {t.report_id && (
              <button onClick={() => onOpenReport(t.report_id)} className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 bg-rose-100 text-rose-700 inline-flex items-center gap-0.5 hover:bg-rose-200" data-testid={`report-link-${t.id}`}>
                <Warning size={9} weight="fill" /> Segnalazione
              </button>
            )}
          </div>
          <p className="text-sm font-bold text-zinc-900">{room?.name || prop?.name || '—'}</p>
          <p className="text-[11px] text-zinc-500">{dateIT(t.check_in)} → {dateIT(t.check_out)} · {t.guest_name || `${t.num_guests || '?'} ospiti`}</p>
          {hasExtras && (
            <div className="flex flex-wrap gap-1 mt-1">
              {(t.extras || []).map(ex => (
                <span key={ex} className="text-[10px] font-bold px-1.5 py-0.5 bg-violet-50 text-violet-700 border border-violet-200">{extraLabels[ex] || ex}</span>
              ))}
            </div>
          )}
          {t.notes && <p className="text-[11px] text-zinc-600 mt-1">📝 {t.notes}</p>}
        </div>
        <div className="flex items-center gap-1.5">
          {!isDone && (
            <button
              onClick={() => onConfigure(t)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-[10px] font-black uppercase tracking-wider transition-all active:scale-95 ${
                needsConfig
                  ? 'bg-amber-500 hover:bg-amber-600 text-white animate-pulse-slow'
                  : 'bg-white border-2 border-zinc-300 hover:border-zinc-900 text-zinc-700'
              }`}
              data-testid={`configure-task-${t.id}`}
            >
              <Gear size={12} weight="bold" />
              {needsConfig ? 'Configura' : 'Modifica'}
            </button>
          )}
          <button onClick={() => onDeleteTask(t.id)} className="p-2 text-zinc-400 hover:text-rose-600" data-testid={`delete-task-${t.id}`} aria-label="Elimina">
            <Trash size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}
